/**
 * Vendor API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  CreateVendorRequest,
  UpdateVendorRequest,
  Vendor,
  VendorListParams,
  VendorListResponse,
  VendorStats,
} from '../types/vendor';

/**
 * Vendor API Endpoints
 * Note: Vendors are organization-scoped, not project-scoped
 */
export const vendorApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // VENDOR CRUD
    // ============================================================================

    // Get all vendors for the organization
    getVendors: builder.query<ApiResponse<VendorListResponse>, VendorListParams | void>({
      query: params => ({
        url: '/vendors',
        params: params || {},
      }),
      providesTags: ['Vendor'],
    }),

    // Get single vendor
    getVendor: builder.query<ApiResponse<Vendor>, string>({
      query: vendorId => ({
        url: `/vendors/${vendorId}`,
      }),
      providesTags: (_result, _error, vendorId) => [{ type: 'Vendor', id: vendorId }],
    }),

    // Create vendor
    createVendor: builder.mutation<ApiResponse<Vendor>, CreateVendorRequest>({
      query: data => ({
        url: '/vendors',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Vendor'],
    }),

    // Update vendor
    updateVendor: builder.mutation<
      ApiResponse<Vendor>,
      { vendorId: string; data: UpdateVendorRequest }
    >({
      query: ({ vendorId, data }) => ({
        url: `/vendors/${vendorId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { vendorId }) => [
        'Vendor',
        { type: 'Vendor', id: vendorId },
      ],
    }),

    // Delete vendor (soft delete)
    deleteVendor: builder.mutation<ApiResponse<void>, string>({
      query: vendorId => ({
        url: `/vendors/${vendorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vendor'],
    }),

    // ============================================================================
    // SEARCH & UTILITIES
    // ============================================================================

    // Search vendors (for autocomplete/dropdown)
    searchVendors: builder.query<ApiResponse<Vendor[]>, { search: string; limit?: number }>({
      query: ({ search, limit = 10 }) => ({
        url: '/vendors/search',
        params: { search, limit },
      }),
      providesTags: ['Vendor'],
    }),

    // Get vendor statistics
    getVendorStats: builder.query<ApiResponse<VendorStats>, void>({
      query: () => ({
        url: '/vendors/stats',
      }),
      providesTags: ['Vendor'],
    }),

    // Get next vendor code
    getNextVendorCode: builder.query<ApiResponse<string>, void>({
      query: () => ({
        url: '/vendors/next-code',
      }),
      // Don't cache this - always fetch fresh
      providesTags: [],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetVendorsQuery,
  useGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  // Search & Utilities
  useSearchVendorsQuery,
  useGetVendorStatsQuery,
  useGetNextVendorCodeQuery,
  // Lazy queries for manual fetching
  useLazyGetVendorsQuery,
  useLazySearchVendorsQuery,
  useLazyGetNextVendorCodeQuery,
} = vendorApi;

/**
 * Re-export types for convenience
 */
export type { VendorListParams, Vendor, VendorStats } from '../types/vendor';
